import { ShieldCheck, Globe, Zap, Radio, BarChart3, Users } from "lucide-react";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AnimatedNumber from "@/components/AnimatedNumber";
import { useEventCounter } from "@/hooks/useEventCounter";

const PROOF_ITEMS = [
  { icon: Globe, label: "190+ countries", desc: "monitored 24/7" },
  { icon: BarChart3, label: "56 tools", desc: "in one platform" },
  { icon: Radio, label: "100+ sources", desc: "OSINT verified" },
  { icon: Zap, label: "<2 min", desc: "alert latency" },
  { icon: ShieldCheck, label: "No rumors", desc: "only verified data" },
];

const SOURCES = ["Reuters", "AP News", "BBC", "Al Jazeera", "ACLED", "SIPRI", "UCDP", "ReliefWeb"];

const MICRO_TESTIMONIALS = [
  { quote: "Replaced my entire morning OSINT routine.", role: "Intelligence Analyst" },
  { quote: "We knew about the airstrike 40 minutes before any news outlet.", role: "NGO Field Coordinator" },
  { quote: "Nothing else comes close at this price point.", role: "Journalist, Middle East" },
];

const SocialProof = () => {
  const recentCount = useEventCounter();
  const [testimonialIdx, setTestimonialIdx] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setTestimonialIdx((i) => (i + 1) % MICRO_TESTIMONIALS.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="border-b border-border bg-card/30">
      <div className="container mx-auto px-4 py-4">
        {/* Stats row */}
        <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6">
          {PROOF_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.label} className="flex items-center gap-1.5 shrink-0">
                <Icon className="h-3.5 w-3.5 text-primary" />
                <span className="text-xs font-semibold text-foreground whitespace-nowrap">{item.label}</span>
                <span className="text-[10px] text-muted-foreground whitespace-nowrap hidden sm:inline">{item.desc}</span>
              </div>
            );
          })}
        </div>

        {/* Live counter + credibility */}
        <div className="mt-3 flex flex-col items-center gap-2 sm:flex-row sm:justify-center sm:gap-6">
          {recentCount > 0 && (
            <div className="flex items-center gap-1.5 text-xs font-mono">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-destructive" />
              </span>
              <AnimatedNumber value={recentCount} className="text-foreground font-bold" />
              <span className="text-muted-foreground">events tracked in the last hour</span>
            </div>
          )}
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Users className="h-3 w-3 text-primary/60" />
            <span>Trusted by security analysts, journalists, and humanitarian organizations in <span className="text-foreground font-semibold">40+ countries</span></span>
          </div>
        </div>

        {/* Micro-testimonial carousel */}
        <div className="mt-3 flex justify-center min-h-[20px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={testimonialIdx}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.25 }}
              className="text-center"
            >
              <span className="text-[11px] italic text-muted-foreground">
                "{MICRO_TESTIMONIALS[testimonialIdx].quote}"
              </span>
              <span className="ml-1.5 text-[10px] text-muted-foreground/50 not-italic">
                — {MICRO_TESTIMONIALS[testimonialIdx].role}
              </span>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Sources */}
        <div className="mt-2 flex flex-wrap items-center justify-center gap-1 text-[10px] text-muted-foreground/40 font-mono">
          <span className="text-muted-foreground/50 mr-1">Aggregating from</span>
          {SOURCES.map((s) => (
            <span key={s} className="shrink-0 px-1.5 py-0.5 rounded bg-muted/30 text-muted-foreground/60 font-semibold">{s}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SocialProof;
